import axios from 'axios';

// Get the API base URL from environment or use default
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const APP_VERSION = process.env.REACT_APP_VERSION || '0.0.0';
const PLATFORM = 'web_provider';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-App-Version': APP_VERSION,
    'X-Platform': PLATFORM,
  },
});


export const apiRequest = async (endpoint, options = {}) => {
  // Get the Firebase token from localStorage
  const token = localStorage.getItem('authToken');

  // Set default headers
  const headers = {
    'Content-Type': 'application/json',
    'X-App-Version': APP_VERSION,
    'X-Platform': PLATFORM,
    ...options.headers,
  };

  // Add authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make the request
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized (token expired or invalid)
    if (response.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('authToken');
      window.location.href = '/login';
      return null;
    }

    // Parse JSON response if the request was successful
    if (response.ok) {
      // Check if response is empty
      const text = await response.text();
      return text ? JSON.parse(text) : {};
    }

    // Handle error responses — surface the status and parsed body so callers can act on
    // server error codes (e.g. the 409 EMAIL_ALREADY_REGISTERED account conflict).
    let body = null;
    try {
      const text = await response.text();
      body = text ? JSON.parse(text) : null;
    } catch (_) {
      body = null;
    }
    const apiError = new Error(`API Error: ${response.status} ${response.statusText}`);
    apiError.status = response.status;
    apiError.body = body;
    throw apiError;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

// Claim flow. These deliberately do NOT go through apiRequest: it discards the error
// body, and the { code, terminal } payload is exactly what decides whether the stored
// bearer token gets thrown away. So they reject with an Error carrying `status` and
// `body` instead.
const claimHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    'X-App-Version': APP_VERSION,
    'X-Platform': PLATFORM,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const unwrapClaim = async (res) => {
  if (res.ok) {
    const text = await res.text();
    return text ? JSON.parse(text) : {};
  }
  let body = null;
  try {
    body = JSON.parse(await res.text());
  } catch (e) {
    // No structured body -- a bare 5xx or a proxy error page. shouldRetainToken()
    // treats a null body as transient, which is the safe default.
  }
  const error = new Error(body?.code || `HTTP ${res.status}`);
  error.status = res.status;
  error.body = body;
  throw error;
};

export const termsApi = {
  getActive: (locale) => apiRequest(`/providers/terms?locale=${encodeURIComponent(locale)}`),
  getStatus: () => apiRequest('/providers/me/terms-status'),
  accept: (version, locale) =>
    apiRequest('/providers/me/terms/accept', {
      method: 'POST',
      body: JSON.stringify({ acceptedTermsVersion: version, acceptedTermsLocale: locale }),
    }),
};

export const claimApi = {
  getChannels: async (providerId) => {
    const res = await fetch(`${API_BASE_URL}/providers/${providerId}/claim-channels`, {
      method: 'GET',
      headers: claimHeaders(),
    });
    return unwrapClaim(res);
  },

  requestProviderClaim: async (providerId, manual = null) => {
    const res = await fetch(`${API_BASE_URL}/providers/${providerId}/claim-requests`, {
      method: 'POST',
      headers: claimHeaders(),
      body: manual ? JSON.stringify(manual) : undefined,
    });
    return unwrapClaim(res);
  },

  // Token path: the raw token from the email link.
  claimWithToken: async (claimToken, acceptedTermsVersion = null, acceptedTermsLocale = null) => {
    const res = await fetch(`${API_BASE_URL}/providers/claim`, {
      method: 'POST',
      headers: claimHeaders(),
      body: JSON.stringify({ claimToken, acceptedTermsVersion, acceptedTermsLocale }),
    });
    return unwrapClaim(res);
  },

  // Verified-email path. Deliberately NOT /providers/claim: only token hashes are
  // stored server-side, so there is no raw token to present here. Authorisation is the
  // caller's verified Firebase address matching the application.
  // This endpoint previously sent NO body at all; it now carries the binding acceptance.
  claimApplication: async (applicationId, acceptedTermsVersion = null, acceptedTermsLocale = null) => {
    const res = await fetch(`${API_BASE_URL}/providers/applications/${applicationId}/claim`, {
      method: 'POST',
      headers: claimHeaders(),
      body: JSON.stringify({ acceptedTermsVersion, acceptedTermsLocale }),
    });
    return unwrapClaim(res);
  },

  // Returns [] rather than throwing on any failure. This runs speculatively on every
  // dashboard load for users who may have nothing to claim; a failure here must not
  // surface an error to someone who never applied.
  listClaimable: async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/providers/claimable`, {
        method: 'GET',
        headers: claimHeaders(),
      });
      if (!res.ok) return [];
      const text = await res.text();
      return text ? JSON.parse(text) : [];
    } catch (e) {
      return [];
    }
  },
};


// Add a request interceptor to add the auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    // Server Pro gate (403 {code: PRO_REQUIRED}) — route the provider to the
    // upgrade view. The Dashboard listens for this event.
    if (error.response?.status === 403 && error.response?.data?.code === 'PRO_REQUIRED') {
      window.dispatchEvent(new Event('anipaw:pro-required'));
    }
    // Server terms gate (403 {code: PROVIDER_TERMS_REQUIRED}). The gate only checks status on
    // mount, so a dashboard left open across a publish would otherwise show generic errors on
    // every write until the page is reloaded. Same backstop shape as PRO_REQUIRED above.
    if (error.response?.status === 403 && error.response?.data?.code === 'PROVIDER_TERMS_REQUIRED') {
      window.dispatchEvent(new Event('anipaw:terms-required'));
    }
    return Promise.reject(error);
  }
);

// Provider API endpoints
export const providerApi = {
  // Get current user's provider data
  getMyProvider: async () => {
    const response = await api.get('/users/me');
    return response.data.provider;
  },

  // Update provider details
  updateProvider: async (providerId, data) => {
    const response = await api.put(`/providers/me`, data);
    return response.data;
  },

  // Owner-confirmation readiness checklist (seeded providers). One validation
  // source: the dialog renders exactly what the backend returns.
  getReadiness: async () => {
    const response = await api.get('/providers/me/readiness');
    return response.data;
  },

  // Confirm the profile (declared-category review + reviewed public contact).
  confirmProfile: async ({ keepDeclaredTypes, publicContactReviewed, publicPhone, publicWebsite }) => {
    const response = await api.post('/providers/me/confirm', {
      keepDeclaredTypes,
      publicContactReviewed,
      publicPhone,
      publicWebsite,
    });
    return response.data;
  },

  // Get provider services
  getProviderServices: async (providerId) => {
    const response = await api.get(`/providers/${providerId}/services`);
    return response.data;
  },

  // Full replace of the owner's service catalogue. Its own endpoint, not part of the
  // whole-provider PUT: `services` is not a field of UpdateMyProviderRequest, so sending it
  // there saved nothing, and putting it back would make the visibility toggle rewrite prices.
  // The server owns currencyCode and providerId -- do not send them.
  updateProviderServices: async (services) => {
    const response = await api.put('/providers/me/services', { services });
    return response.data;
  },

  // Owner services full-replace: PUT /providers/me/services
  updateMyServices: async (services) => {
    const response = await api.put('/providers/me/services', services);
    return response.data;
  },

  // Store commission this provider pays (STORE only; 403 otherwise, 404 when there is no profile).
  // FLAT -> { mode, ratePercent, source }; TIERED adds last/this month volume, currencyCode and
  // nextTier ({ threshold, ratePercent, remainingThisMonth } | null on the top tier).
  getMyCommission: async () => {
    const response = await api.get('/providers/me/commission');
    return response.data;
  },

  // GET /providers/me/payouts/summary -> { heldTotal, currencyCode, nextReleaseAt | null,
  // heldOrders, byCurrency: [{ currencyCode, amount }] } for the caller's own store.
  getMyPayoutsSummary: async () => {
    const response = await api.get('/providers/me/payouts/summary');
    return response.data;
  },

  // Get referral count (for influencers)
  getReferralCount: async () => {
    const response = await api.get('/providers/referrals/count');
    return response.data;
  },

  // Get provider orders
  getProviderOrders: async (providerId, page = 0, size = 10) => {
    const response = await api.get(`/providers/${providerId}/orders`, {
      params: { page, size }
    });
    return response.data;
  },

  // Get all transactions for provider's orders
  getProviderTransactions: async (providerId) => {
    const response = await api.get(`/providers/${providerId}/transactions`);
    return response.data;
  },

  // Get a single order request by ID
  getOrder: async (orderId) => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },

  /**
   * SERVICE bookings of the caller's provider that overlap [from, to) — the calendar's data source.
   * Complete for the range (unlike getProviderOrders, which is the newest 500). ISO instants.
   */
  getProviderCalendar: async (from, to) => {
    const response = await api.get('/order-requests/provider/calendar', { params: { from, to } });
    return response.data;
  },

  // ---- Provider-private order notes (never visible to the customer) ----------------------------
  getOrderNotes: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/notes`);
    return response.data;
  },
  addOrderNote: async (orderId, body) => {
    const response = await api.post(`/orders/${orderId}/notes`, { body });
    return response.data;
  },
  updateOrderNote: async (orderId, noteId, body) => {
    const response = await api.put(`/orders/${orderId}/notes/${noteId}`, { body });
    return response.data;
  },
  deleteOrderNote: async (orderId, noteId) => {
    await api.delete(`/orders/${orderId}/notes/${noteId}`);
  },
  /** [{ orderRequestId, count, latestBody, latestAt, searchText }] for every one of my orders that has notes. */
  getOrderNotesSummary: async () => {
    const response = await api.get('/orders/notes/summary');
    return response.data;
  },

  // Get chat messages for an order
  getOrderMessages: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/messages`);
    return response.data;
  },

  // Send a chat message on an order (type: TEXT | QUOTE | QUOTE_REQUEST | BOOKING | CANCEL)
  sendOrderMessage: async (orderId, body, type = 'TEXT') => {
    const response = await api.post(`/orders/${orderId}/messages`, {
      body,
      orderRequestId: orderId,
      type,
    });
    return response.data;
  },

  /**
   * Submit a full PROPOSAL (order-creation design §B), not just a price.
   *
   * `serviceStartLocal` is a BARE provider-local wall clock — `2026-09-20T14:30:00`, no offset,
   * no zone. The server resolves it in the provider's effective zone, so this client does no
   * timezone conversion at all and must NEVER send a Date's ISO string: that carries the
   * BROWSER's offset and the server rejects it.
   *
   * The field is deliberately not called `serviceStartAt` — that name means an absolute instant
   * on the response DTO, and a shared serializer would append a Z to it.
   */
  sendQuote: async (orderId, { providerDogServiceId, amount, serviceStartLocal, numOfDays, dogIds, expiresAt, body }) => {
    const response = await api.post(`/order-requests/${orderId}/quote`, {
      providerDogServiceId,
      amount,
      serviceStartLocal,
      numOfDays,
      dogIds,
      ...(expiresAt ? { expiresAt } : {}),
      ...(body ? { body } : {}),
    });
    return response.data;
  },

  /** Withdraw the pending quote — a typed action, not a fake price. */
  declineQuote: async (orderId) => {
    const response = await api.post(`/order-requests/${orderId}/quote/decline`);
    return response.data;
  },

  // Perform order action (complete a SERVICE job, cancel). STORE orders are no longer completed
  // through complete-job (it answers 400): they use shipStoreOrder / deliverStoreOrder below.
  performOrderAction: async (orderId, action) => {
    let endpoint;
    switch (action) {
      case 'complete':
        endpoint = `/orders/${orderId}/complete-job`;
        break;
      case 'cancel':
        endpoint = `/orders/${orderId}/provider-cancel`;
        break;
      default:
        throw new Error('Invalid action');
    }

    const response = await api.post(endpoint);
    return response.data;
  },

  // Provider records that an OFFLINE booking was paid. Idempotent server-side; returns the
  // full order DTO (same shape as complete-job), so callers may replace their loaded order.
  markOrderPaid: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/mark-paid`);
    return response.data;
  },

  // ---- Store fulfilment & returns (SDC WS7) -------------------------------------------------

  /** Mark a paid STORE order shipped. Carrier and tracking number are optional (max 100 chars each). */
  shipStoreOrder: async (orderId, { carrier, trackingNumber } = {}) => {
    const body = {};
    if (carrier && carrier.trim()) body.carrier = carrier.trim();
    if (trackingNumber && trackingNumber.trim()) body.trackingNumber = trackingNumber.trim();
    const response = await api.post(`/order-requests/${orderId}/store/ship`, body);
    return response.data;
  },

  /** Mark a SHIPPED STORE order delivered. Starts the buyer's return window. */
  deliverStoreOrder: async (orderId) => {
    const response = await api.post(`/order-requests/${orderId}/store/deliver`);
    return response.data;
  },

  /**
   * The store's return queue: [{ returnRequest, order }]. Default (no statuses) is the open set
   * REQUESTED, APPROVED, ESCALATED; pass ['REJECTED','REFUNDED','CANCELLED'] for the history.
   */
  getStoreReturns: async ({ statuses, page = 0, size = 50 } = {}) => {
    const params = { page, size };
    if (statuses && statuses.length) params.status = statuses.join(',');
    const response = await api.get('/order-requests/store/returns', { params });
    return response.data;
  },

  /** { count } of return requests waiting for the store's answer (REQUESTED only): the nav badge. */
  getOpenStoreReturnsCount: async () => {
    const response = await api.get('/order-requests/store/returns/open-count');
    return response.data;
  },

  /**
   * Approve and refund. amount null/undefined = everything still refundable; less needs a note.
   * Refunds synchronously. Repeating the call is safe (idempotent), which is also how a request
   * left APPROVED by an unknown gateway outcome is finished.
   */
  approveStoreReturn: async (orderId, returnId, { amount, restock = false, note } = {}) => {
    const body = { restock };
    if (amount !== undefined && amount !== null) body.amount = amount;
    if (note && note.trim()) body.note = note.trim();
    const response = await api.post(`/order-requests/${orderId}/store/returns/${returnId}/approve`, body);
    return response.data;
  },

  /** Reject a return request; the note is required. */
  rejectStoreReturn: async (orderId, returnId, { note }) => {
    const response = await api.post(`/order-requests/${orderId}/store/returns/${returnId}/reject`, { note });
    return response.data;
  },

  // Delivery zone endpoints
  getDeliveryZone: async (providerId) => {
    const response = await api.get(`/providers/${providerId}/delivery-zone`);
    return response.data;
  },

  updateDeliveryZone: async (providerId, points) => {
    const response = await api.post(`/providers/${providerId}/delivery-zone`, { points });
    return response.data;
  },

  clearDeliveryZone: async (providerId) => {
    const response = await api.delete(`/providers/${providerId}/delivery-zone`);
    return response.data;
  },

  // Store delivery settings (owner/admin). Resolves to null when the store has not set delivery up
  // yet (the server answers 204). A 400 carries { error } naming the offending field or ring.
  getDeliverySettings: async (providerId) => {
    const response = await api.get(`/providers/${providerId}/delivery-settings`);
    return response.status === 204 || !response.data ? null : response.data;
  },

  // The store currency as the server resolves it, readable before delivery is configured.
  // Resolves to the currency code, or null when the server cannot tell it.
  getStoreCurrency: async (providerId) => {
    const response = await api.get(`/providers/${providerId}/delivery-settings/store-currency`);
    return response.data?.currencyCode || null;
  },

  // Full replacement of the settings (and of the distance rings when pricingMode is DISTANCE).
  // Never send currencyCode: the server resolves the store currency itself.
  updateDeliverySettings: async (providerId, settings) => {
    const response = await api.put(`/providers/${providerId}/delivery-settings`, settings);
    return response.data;
  },

  // Audience: users referred via affiliate link
  getMyAffiliates: async () => {
    const response = await api.get('/share/referrals');
    return response.data;
  },

  // Audience: users who liked the provider's profile
  getMyLikers: async () => {
    const response = await api.get('/providers/me/likers');
    return response.data;
  },

  // PayPal onboarding
  getPaypalOnboardingLink: async () => {
    const response = await api.post('/payments/paypal/onboarding-link');
    return response.data;
  },

  getPaypalOnboardingStatus: async () => {
    const response = await api.get('/payments/paypal/onboarding-status');
    return response.data;
  },

  disconnectPaypalPayout: async () => {
    await api.delete('/payments/paypal/onboarding');
  },

  // Pro analytics (design §4.5): daily series + totals; from/to ISO dates.
  getMyAnalytics: async (fromDate, toDate) => {
    const response = await api.get('/providers/me/analytics', {
      params: { from: fromDate, to: toDate },
    });
    return response.data;
  },

  // Pro reviews (read-only).
  getMyReviews: async (page = 0, size = 20) => {
    const response = await api.get('/providers/me/reviews', { params: { page, size } });
    return response.data;
  },

  getMyReviewsSummary: async () => {
    const response = await api.get('/providers/me/reviews/summary');
    return response.data;
  },

  // Reply to a review on your own profile. Create and edit are ONE idempotent PUT: there is at
  // most one reply per review, so a POST/PUT split would only invite 409s on retry.
  upsertReviewReply: async (reviewId, body) => {
    const response = await api.put(`/providers/me/reviews/${reviewId}/reply`, { body });
    return response.data;
  },

  // Withdraws the reply. Soft-deleted server-side, retaining any moderation record, so a later
  // re-post cannot clear an admin hide.
  deleteReviewReply: async (reviewId) => {
    await api.delete(`/providers/me/reviews/${reviewId}/reply`);
  },

  // Active Pro terms version — registerAsPro rejects any other value (design §7).
  getProTermsVersion: async () => {
    const response = await api.get('/providers/pro/terms-version');
    return response.data;
  },

  getMyPromotions: async () => {
    const response = await api.get('/providers/me/promotions');
    return response.data;
  },

  pausePromotion: async (postId) => {
    const response = await api.post(`/posts/${postId}/promotion/pause`);
    return response.data;
  },

  resumePromotion: async (postId) => {
    const response = await api.post(`/posts/${postId}/promotion/resume`);
    return response.data;
  },

  activatePromotion: async (postId) => {
    const response = await api.post(`/posts/${postId}/promotion/activate`);
    return response.data;
  },

  getCreditAccount: async () => {
    const response = await api.get('/providers/me/credit-account');
    return response.data;
  },

  getTopUpHistory: async () => {
    const response = await api.get('/providers/me/credit-account/top-ups');
    return response.data;
  },

  // Billing products: credit packs and subscription pricing from the server.
  // kind: 'CREDIT_PACK' | 'SUBSCRIPTION'
  getBillingProducts: async (params = {}) => {
    const response = await api.get('/providers/billing-products', {
      params: { channel: 'PAYPAL', kind: 'CREDIT_PACK', ...params },
    });
    return response.data;
  },

  // Ad-credit top-up: initiate (returns approveUrl), then capture on return.
  initiateCreditTopUp: async (amountCents) => {
    const response = await api.post('/providers/me/credit-account/top-up', { amountCents });
    return response.data;
  },

  captureCreditTopUp: async (topUpId) => {
    const response = await api.post(`/providers/me/credit-account/top-up/${topUpId}/capture`);
    return response.data;
  },

  // Pro subscription lifecycle (spec §4.3 / §4.6). Errors carry a stable `reason` code
  // (e.response.data.message or e.response.data.error) that UpgradePage switches on.
  createProSubscription: async () => {
    const response = await api.post('/providers/pro/subscription');
    return response.data; // { approvalUrl, subscription? }
  },
  confirmProSubscription: async (subscriptionId) => {
    const response = await api.post('/providers/pro/subscription/confirm', { subscriptionId });
    return { status: response.status, ...response.data }; // 200 Active | 202 Pending
  },
  getProSubscription: async () => {
    const response = await api.get('/providers/pro/subscription');
    return response.data; // { subscription: {...}|null, checkout: { state }|null }
  },
  cancelProSubscription: async () => {
    const response = await api.post('/providers/pro/subscription/cancel');
    return { status: response.status }; // 200 Canceled | 202 InProgress
  },
  getRuntimeConfig: async () => {
    const response = await api.get('/app-config');
    return response.data; // { paymentsEnabled, bookingSettlementOffline }
  },

  // Self-serve Pro activation (requires SERVICE type, completed PayPal payout
  // onboarding, and the active terms version).
  registerAsPro: async (termsVersion) => {
    const response = await api.post('/providers/pro/register', { termsVersion });
    return response.data;
  },

  // Pro-gated availability full-replace (design §4.7) — both keys always sent.
  updateAvailability: async ({ availabilityDates, availabilityRadius }) => {
    const response = await api.put('/providers/me/availability', {
      availabilityDates,
      availabilityRadius: availabilityRadius ?? null,
    });
    return response.data;
  },

  // Server-verified onboarding (replaces the removed client-trusted complete-onboarding):
  // merchantIdInPayPal is only a lookup key — the backend verifies PayPal's tracking_id and
  // persists two-phase (PENDING -> COMPLETED). Call with no argument to re-check the stored id.
  verifyPaypalOnboarding: async (merchantIdInPayPal) => {
    const response = await api.post(
      '/payments/paypal/verify-onboarding',
      merchantIdInPayPal ? { merchantIdInPayPal } : {}
    );
    return response.data;
  },
};

// Product API endpoints  
export const productApi = {
  // Get products by provider
  getProductsByProvider: async (providerId, page = 0, size = 20) => {
    const response = await api.get(`/products/store/${providerId}`, {
      params: { page, size }
    });
    return response.data;
  },

  // Create product
  createProduct: async (providerId, productData) => {
    const response = await api.post(`/products/store/${providerId}`, productData);
    return response.data;
  },

  // Update product
  updateProduct: async (productId, providerId, productData) => {
    const response = await api.put(`/products/${productId}/store/${providerId}`, productData);
    return response.data;
  },

  // Delete product
  deleteProduct: async (productId, providerId) => {
    const response = await api.delete(`/products/${productId}/store/${providerId}`);
    return response.data;
  },

  // Get product stats
  getProductStats: async (providerId) => {
    const response = await api.get(`/products/store/${providerId}/stats`);
    return response.data;
  },

  // Bulk import products from JSON
  importProducts: async (providerId, jsonData) => {
    const response = await api.post(`/products/store/${providerId}/import`, jsonData);
    return response.data;
  },
};

// Category API endpoints
export const categoryApi = {
  // Get all categories
  getAllCategories: async (locale = null) => {
    const params = locale ? { locale } : {};
    const response = await api.get('/categories', { params });
    return response.data;
  },

  // Get root categories  
  getRootCategories: async (locale = null) => {
    const params = locale ? { locale } : {};
    const response = await api.get('/categories/root', { params });
    return response.data;
  },

  // Get category by ID
  getCategoryById: async (id, locale = null) => {
    const params = locale ? { locale } : {};
    const response = await api.get(`/categories/${id}`, { params });
    return response.data;
  },

  // Get subcategories
  getSubCategories: async (parentId, locale = null) => {
    const params = locale ? { locale } : {};
    const response = await api.get(`/categories/${parentId}/subcategories`, { params });
    return response.data;
  },

  // Create category
  createCategory: async (categoryData) => {
    const response = await api.post('/categories', categoryData);
    return response.data;
  },

  // Update category
  updateCategory: async (id, categoryData) => {
    const response = await api.put(`/categories/${id}`, categoryData);
    return response.data;
  },
};

// Image API endpoints
export const imageApi = {
  // Upload single image
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Upload multiple images
  uploadMultipleImages: async (files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await api.post('/images/upload-multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get image URL
  getImageUrl: async (name, size = 'medium') => {
    const response = await api.get('/images/url', {
      params: { name, size }
    });
    return response.data.url;
  },
};

// Video API endpoints — provider media uses the same authenticated upload pipeline as posts.
export const videoApi = {
  uploadVideo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/videos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Location API endpoints
export const locationApi = {
  // Get currency information by country name
  getCurrencyByCountry: async (countryName) => {
    const response = await api.get(`/location/countries/${countryName}/currency`);
    return response.data;
  },
};

export default api;
